import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import useSeo from "@/lib/useSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [loading, setLoading] = useState(false);

  useSeo({
    title: "Create Your Free Hook2Income AI Account — Start Building Courses & Campaigns",
    description:
      "Sign up free for Hook2Income AI. Create a brand voice once, then spin up mini-courses and full social campaigns in minutes. Beginner-friendly by design.",
    path: "/register",
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, inviteCode);
      toast.success("Account created!");
      navigate("/brand");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-foreground p-12 text-background lg:flex">
        <div className="flex items-center gap-2.5">
          <img src="/logo-mark.png" alt="Hook2Income AI" className="h-10 w-10 object-contain" />
          <span className="font-head text-xl font-bold">
            Hook2Income<span style={{ color: "hsl(var(--gold))" }}> AI</span>
          </span>
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold leading-tight lg:text-5xl">
            Your content engine starts here.
          </h1>
          <p className="mt-6 max-w-md text-base text-background/70">
            Create a brand voice once, then spin up mini courses and full social campaigns in minutes.
          </p>
        </div>
        <p className="text-sm text-background/50">Free to start. Beginner-friendly by design.</p>
      </div>

      <div className="flex items-center justify-center bg-background px-6 py-12">
        <div className="w-full max-w-sm fade-up">
          <h2 className="font-head text-3xl font-bold">Create account</h2>
          <p className="mt-2 text-sm text-muted-foreground">Start building campaigns for free.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" data-testid="register-name" value={name}
                onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="register-email" value={email}
                onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="register-password" value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" required minLength={6} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite">Invite code</Label>
              <Input id="invite" data-testid="register-invite-code" value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)} placeholder="Enter your invite code" required />
            </div>
            <Button type="submit" disabled={loading} data-testid="register-submit"
              className="w-full rounded-full bg-primary py-6 text-base font-semibold hover:bg-primary/90">
              {loading ? "Creating..." : <>Create account <ArrowRight size={18} className="ml-1" /></>}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" className="font-semibold text-primary hover:underline" data-testid="go-login">
              Sign in
            </Link>
          </p>
          <p className="mt-3 text-center text-sm text-muted-foreground">
            <Link to="/about" className="hover:underline" data-testid="register-about-link">About Hook2Income</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
